import { INTAKE_ASSESSMENT_FLOW_NAME } from 'constants/flows';
import { courseRuntimeUrl, capturedCourseRuntimeUrl, CapturedCourseContext } from './courseRuntimeContext';
import api from '../httpClient';
import type {
  Flow,
  FlowRun,
  FlowCompletionStatus,
  FlowRequirementsMet,
  ResponsePayload,
} from '../../types';
import { encodeFlowSegment, preventDuplicateCalls } from './helpers';

export interface FlowSubmissionAnswer {
  response_id: number | null;
  expected_updated_at: string | null;
  response: ResponsePayload;
}

export interface FlowSubmissionRequest {
  flow_run_id: number;
  request_id: string;
  answers: FlowSubmissionAnswer[];
}

export interface FlowSubmissionContinuation {
  course: { id: number; name: string } | null;
  course_completed: boolean;
  course_completed_at: string | null;
  chapter: { id: number; title: string } | null;
  next_flow: { id: number; name: string; title: string; chapter: { id: number; title: string } | null } | null;
}

export interface FlowSubmissionResult {
  progress: FlowRun;
  responses: ResponsePayload[];
  continuation: FlowSubmissionContinuation;
}

export type FlowCompletionContext = 'runtime' | 'system-intake';

/** Only the app-wide onboarding check is independent of the open course. */
export const flowCompletionStatusUrl = (
  flowName: string,
  context: FlowCompletionContext = 'runtime',
): string => {
  const path = `/api/flows/${encodeURIComponent(flowName)}/completion-status/`;
  if (context === 'system-intake') {
    if (flowName !== INTAKE_ASSESSMENT_FLOW_NAME) {
      throw new Error('System intake completion context is only valid for Intake Assessment.');
    }
    return path;
  }
  return courseRuntimeUrl(path);
};

export const flows = {
  submit: (flowName: string, data: FlowSubmissionRequest, context?: CapturedCourseContext) => {
    const path = `/api/flows/${encodeURIComponent(flowName)}/submit/`;
    return api.post<FlowSubmissionResult>(context ? capturedCourseRuntimeUrl(path, context) : courseRuntimeUrl(path), data);
  },
  getAll: () => api.get<Flow[]>(courseRuntimeUrl('/api/flows/')),
  get: (id: number) => api.get<Flow>(courseRuntimeUrl(`/api/flows/${id}/`)),
  /**
   * Flow detail. Pass the course the learner opened the flow from so the
   * returned `theme` is that course's (when the flow doesn't set its own).
   * `courseRuntimeUrl` rejects a course id that conflicts with the route.
   */
  getFlow: (flowName: string, options?: { courseId?: number | null }) => {
    const courseId = options?.courseId;
    const query = typeof courseId === 'number' && Number.isSafeInteger(courseId) && courseId > 0
      ? `?course_id=${courseId}`
      : '';
    return api.get<Flow>(courseRuntimeUrl(`/api/flows/${encodeURIComponent(flowName)}/${query}`));
  },
  getQuestions: (
    flowIdentifier: string | number,
    options?: { filtered?: boolean },
  ) =>
    api.get(courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowIdentifier)}/questions/`), {
      params: options?.filtered ? { filtered: true } : undefined,
    }),
  getFlowRun: (
    flowName: string,
    params?: { targetDate?: string; flowRunId?: number | null },
  ) =>
    api.get<FlowRun>(courseRuntimeUrl(`/api/flows/${encodeURIComponent(flowName)}/progress/`), {
      params: {
        ...(params?.targetDate ? { target_date: params.targetDate } : {}),
        ...(typeof params?.flowRunId === 'number' ? { flow_run_id: params.flowRunId } : {}),
      },
    }),
  updateProgress: (
    flowName: string,
    data: {
      question_id: number;
      current_index?: number;
      flow_run_id?: number | null;
      target_date?: string;
    },
  ) => {
    const payload: Record<string, unknown> = {
      question_id: data.question_id,
      ...(typeof data.current_index === 'number' ? { current_index: data.current_index } : {}),
    };

    if (typeof data.flow_run_id === 'number') {
      payload.flow_run_id = data.flow_run_id;
    }

    if (typeof data.target_date === 'string' && data.target_date.length > 0) {
      payload.target_date = data.target_date;
    }

    return api.post(courseRuntimeUrl(`/api/flows/${encodeURIComponent(flowName)}/progress/`), payload);
  },
  updateInteractionMode: (
    flowName: string,
    data: {
      flowRunId: number;
      mode: 'standard' | 'voice';
    },
  ) =>
    api.post<{
      flow_run_id: number;
      last_interaction_mode: 'standard' | 'voice';
    }>(
      courseRuntimeUrl(`/api/flows/${encodeURIComponent(flowName)}/mode/`),
      {
        flow_run_id: data.flowRunId,
        mode: data.mode,
      },
    ),
  startFlow: (flowName: string, data?: { question_id?: number }) =>
    api
      .post(courseRuntimeUrl(`/api/flows/${encodeURIComponent(flowName)}/progress/`), data || {})
      .then((response) => response.data),
  resetFlow: (flowName: string) =>
    api.delete(courseRuntimeUrl(`/api/flows/${encodeURIComponent(flowName)}/progress/`)),
  restartFlow: (flowName: string) => {
    const encodedName = encodeURIComponent(flowName);
    return api
      .delete(courseRuntimeUrl(`/api/flows/${encodedName}/progress/`))
      .then(() => api.post(courseRuntimeUrl(`/api/flows/${encodedName}/progress/`)));
  },
  getRemainingCount: (flowName: string) =>
    api.get(courseRuntimeUrl(`/api/flows/${encodeURIComponent(flowName)}/remaining-count/`)),
  create: (() => {
    const prevent = preventDuplicateCalls();
    return (data: any) => prevent(() => api.post('/api/admin/flows/', data));
  })(),
  update: (id: number, data: any) => api.put(`/api/admin/flows/${id}/`, data),
  delete: (id: number) => api.delete(`/api/admin/flows/${id}/`),
  addQuestion: (flowId: number, data: { question_id: number; order: number }) =>
    api.post(`/api/admin/flows/${flowId}/add_question/`, data),
  removeQuestion: (flowId: number, questionId: number) =>
    api.delete(`/api/admin/flows/${flowId}/remove_question/`, {
      data: { question_id: questionId },
    }),
  reorderQuestions: (flowId: number, questionIds: number[]) =>
    api.post(`/api/admin/flows/${flowId}/reorder_questions/`, {
      question_ids: questionIds,
    }),
  getAdminFlows: () => api.get('/api/admin/flows/'),
  getAdminFlow: (id: number) => api.get(`/api/admin/flows/${id}/`),
  deactivate: (flowId: number) => api.post(`/api/admin/flows/${flowId}/deactivate/`),
  deleteFlow: (flowId: number) => api.delete(`/api/admin/flows/${flowId}/delete_flow/`),
  getFlowStats: (flowName: string) =>
    api.get(courseRuntimeUrl(`/api/flows/${encodeURIComponent(flowName)}/stats/`)),
  getCompletionStatus: (flowName: string, context: FlowCompletionContext = 'runtime') =>
    api
      .get<FlowCompletionStatus>(
        flowCompletionStatusUrl(flowName, context),
      )
      .then((response) => response.data),
  checkRequirementsMet: (flowName: string) =>
    api
      .get<FlowRequirementsMet>(
        courseRuntimeUrl(`/api/flows/${encodeURIComponent(flowName)}/requirements-met/`),
      )
      .then((response) => response.data),
};
